'use client'

import { ArrowLeft, ChevronDown, Menu, MoreVertical, Pencil, X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react'

import { AppTabs } from '@client/components/layout/AppTabs'
import { ImpersonationBanner } from '@client/components/layout/ImpersonationBanner'
import { IncidentBanner } from '@client/components/layout/IncidentBanner'
import { UserMenu } from '@client/components/layout/UserMenu'
import { Button } from '@client/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@client/components/ui/dropdown-menu'
import { MARKETING_NAV } from '@client/lib/marketing-nav'
import { useHeader, type Breadcrumb, type HeaderAction, type NavItem } from '@client/providers/header-provider'
import { useSelectedProject } from '@client/providers/project-provider'

/**
 * The little flag on a header action — "New", "Beta".
 *
 * Amber rather than the primary colour: primary is what every other control in
 * this header already is, so a primary badge reads as more button rather than
 * as a flag. `amber-400` on `amber-950` clears contrast on both grounds, so it
 * needs no dark-mode variant.
 *
 * Upright, not tilted. A tilt was tried and read as misalignment rather than
 * as a flourish — at 10px inside a 32px button there isn't the room for it to
 * look deliberate.
 *
 * Shared by both renderers below so they can't drift apart.
 */
function ActionBadge({ label }: { label: string }) {
  return (
    <span className="ml-2 inline-block rounded-full bg-amber-400 px-[0.4375rem] py-[0.1875rem] text-[10px] font-bold uppercase leading-none tracking-[0.03em] text-amber-950">
      {label}
    </span>
  )
}

function HeaderActions({ actions }: { actions: HeaderAction[] }) {
  if (actions.length === 0) return null

  return (
    <>
      {/* Desktop: individual buttons */}
      <div className="hidden sm:flex items-center gap-3">
        {actions.map(({ key, label, icon: Icon, onClick, disabled, title, badge, ...rest }) => (
          <span key={key} title={disabled ? title : undefined}>
            <Button variant="outline" size="sm" onClick={onClick} disabled={disabled} data-testid={rest['data-testid']}>
              <Icon className="w-4 h-4 mr-2" />
              {label}
              {badge && <ActionBadge label={badge} />}
            </Button>
          </span>
        ))}
      </div>

      {/* Mobile: dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="sm:hidden">
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {actions.map(({ key, label, icon: Icon, onClick, disabled, badge }) => (
            <DropdownMenuItem key={key} onClick={onClick} disabled={disabled}>
              <Icon className="w-4 h-4 mr-2" />
              {label}
              {badge && <ActionBadge label={badge} />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

/**
 * The final breadcrumb when it's renameable: shows the bold label with a pencil
 * that appears on hover; clicking either swaps in an input. Enter / blur saves,
 * Esc cancels, and the field selects its contents on focus so typing replaces.
 */
function EditableCrumb({ label, onRename }: { label: string; onRename: (newLabel: string) => void | Promise<void> }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(label)

  // Keep the draft in sync when the resolved label changes (e.g. after a save
  // or a background refetch) while we're not actively editing.
  useEffect(() => {
    if (!editing) setDraft(label)
  }, [label, editing])

  const commit = () => {
    setEditing(false)
    const trimmed = draft.trim()
    if (trimmed && trimmed !== label) void onRename(trimmed)
    else setDraft(label)
  }

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') {
            setDraft(label)
            setEditing(false)
          }
        }}
        maxLength={255}
        aria-label="Rename"
        title="Enter to save · Esc to cancel"
        className="font-bold text-foreground bg-transparent border-b border-foreground/40 outline-none max-w-[300px] min-w-0"
      />
    )
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft(label)
        setEditing(true)
      }}
      title="Rename"
      className="group/rename inline-flex min-w-0 items-center gap-1.5 font-bold text-foreground"
    >
      <span className="truncate max-w-[300px]">{label}</span>
      <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/rename:opacity-100" />
    </button>
  )
}

function BreadcrumbBar({ breadcrumbs, accessory }: { breadcrumbs: Breadcrumb[]; accessory?: ReactNode }) {
  return (
    <div className="border-b border-border px-4 sm:px-6 lg:px-8 py-2">
      <div className="flex items-center gap-2 text-base min-w-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {breadcrumbs.map((crumb, i) => (
            <span key={i} className="flex items-center gap-2 min-w-0">
              {i > 0 && <span className="text-muted-foreground shrink-0">/</span>}
              {crumb.href ? (
                <Link
                  href={crumb.href}
                  className="text-muted-foreground hover:text-foreground underline transition-colors truncate max-w-[200px]"
                >
                  {crumb.label}
                </Link>
              ) : crumb.onRename ? (
                <EditableCrumb label={crumb.label} onRename={crumb.onRename} />
              ) : (
                <span className="font-bold text-foreground truncate max-w-[300px]">{crumb.label}</span>
              )}
            </span>
          ))}
        </div>
        {accessory && <div className="flex-shrink-0">{accessory}</div>}
      </div>
    </div>
  )
}

function NavDropdown({ item }: { item: NavItem }) {
  if (item.href) {
    return (
      <Link
        href={item.href}
        className="hidden sm:block text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {item.label}
      </Link>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="hidden sm:flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors outline-none">
        {item.label}
        <ChevronDown className="h-3.5 w-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {item.items!.map((sub) => (
          <DropdownMenuItem key={sub.href} asChild>
            <Link href={sub.href} className="cursor-pointer flex items-center gap-2">
              {sub.icon && <sub.icon className="h-4 w-4 text-muted-foreground" />}
              {sub.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function GlobalHeader() {
  const [navMenuOpen, setNavMenuOpen] = useState(false)
  const { data: session } = useSession()
  const router = useRouter()
  const pathname = usePathname()
  const { actions, backHref, backLabel, showBackHome, breadcrumbs, navItems, accessory, breadcrumbAccessory } =
    useHeader()
  const { selected } = useSelectedProject()
  const headerRef = useRef<HTMLElement>(null)

  const resolvedBackHref = showBackHome ? selected.homePath : backHref
  const resolvedBackLabel = showBackHome ? 'Dashboard' : backLabel

  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0
  const pageHasNav = navItems && navItems.length > 0
  const logoHref =
    selected.kind === 'personal' && (pathname === '/projects' || pathname.startsWith('/projects/'))
      ? '/projects'
      : session
        ? selected.homePath
        : '/'
  const isSuperadmin = session?.user?.meta?.role === 'superadmin'
  // Superadmin escape hatch: right-clicking the logo jumps to /admin instead of
  // showing the native context menu. Regular users get the browser default.
  const onLogoContextMenu = isSuperadmin
    ? (e: MouseEvent) => {
        e.preventDefault()
        router.push('/admin')
      }
    : undefined
  // Authenticated users on app pages get a hamburger for marketing nav;
  // marketing pages use their own page-level nav inline.
  const showSiteNavToggle = !!session && !pageHasNav
  // The nav items shown inline in the header bar (marketing pages only)
  const hasInlineNav = pageHasNav

  // Keep a CSS variable in sync with the header's rendered height so downstream
  // consumers (pt-[var(--header-h)], calc(100vh - var(--header-h))) adapt automatically.
  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const update = () => document.documentElement.style.setProperty('--header-h', `${el.offsetHeight}px`)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <header
      ref={headerRef}
      className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border"
    >
      <IncidentBanner />
      <ImpersonationBanner />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-full">
        <div className="flex items-center justify-between h-16">
          {/* Left: back + logo + nav */}
          <nav className="flex items-center gap-2 sm:gap-8 min-w-0">
            {!hasBreadcrumbs && resolvedBackHref && (
              <Link
                href={resolvedBackHref}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
                {resolvedBackLabel && <span className="hidden sm:inline">{resolvedBackLabel}</span>}
              </Link>
            )}

            <Link
              href={logoHref}
              onContextMenu={onLogoContextMenu}
              title={isSuperadmin ? 'Right-click for admin dashboard' : undefined}
              // min-h/min-w keep the tap target at 48px. On mobile the wordmark
              // is hidden, so without this the whole link is just the 20px logo
              // image — the smallest touch target on the site.
              className="flex items-center gap-2 font-semibold text-lg shrink-0 min-h-12 min-w-12 sm:min-w-0"
            >
              <Image src="/Logo.png" alt="Reviewer3" width={20} height={20} className="h-5 w-auto opacity-90" />
              {/* Wordmark hidden on mobile to save header space; logo carries the brand */}
              <span className="hidden sm:inline text-foreground">Reviewer3</span>
            </Link>

            {hasInlineNav && navItems!.map((item) => <NavDropdown key={item.label} item={item} />)}
            {/* Signed-in dashboard tabs (Reviews · Feed); renders nothing outside the dashboard. */}
            {!hasInlineNav && session && <AppTabs />}
          </nav>

          {/* Right: accessory + actions + CTA + site nav toggle + user menu */}
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {accessory}
            <HeaderActions actions={actions} />
            {hasInlineNav && !session && (
              <Link
                href="/upload"
                className="hidden sm:inline-flex items-center justify-center bg-foreground text-background h-9 px-4 text-sm font-medium rounded hover:bg-foreground/90 transition-colors"
              >
                Try Reviewer3
              </Link>
            )}
            {/* Mobile hamburger for marketing page nav */}
            {hasInlineNav && (
              <button
                className="sm:hidden flex items-center justify-center min-h-12 min-w-12 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setNavMenuOpen(!navMenuOpen)}
                aria-label="Toggle menu"
              >
                {navMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            )}
            {/* Dropdown for authenticated users on app pages — shows marketing nav */}
            {showSiteNavToggle && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center justify-center min-h-12 min-w-12 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Site navigation"
                  >
                    <Menu className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {MARKETING_NAV.map((item) =>
                    item.items ? (
                      item.items.map((sub) => (
                        <DropdownMenuItem key={sub.href} asChild>
                          <Link href={sub.href} className="cursor-pointer flex items-center gap-2">
                            {sub.icon && <sub.icon className="h-4 w-4 text-muted-foreground" />}
                            {sub.label}
                          </Link>
                        </DropdownMenuItem>
                      ))
                    ) : item.href ? (
                      <DropdownMenuItem key={item.href} asChild>
                        <Link href={item.href} className="cursor-pointer">
                          {item.label}
                        </Link>
                      </DropdownMenuItem>
                    ) : null
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <UserMenu />
          </div>
        </div>
      </div>

      {/* Mobile nav panel for marketing pages only */}
      {navMenuOpen && hasInlineNav && (
        <div className="bg-background border-b border-border sm:hidden">
          <nav className="container max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {navItems!.map((item) => (
              <div key={item.label}>
                {item.items ? (
                  <>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-2 pt-2 pb-1">
                      {item.label}
                    </p>
                    {item.items.map((sub) => (
                      <Link
                        key={sub.href}
                        href={sub.href}
                        className="block text-sm font-medium text-foreground hover:text-muted-foreground transition-colors py-2 px-2 rounded hover:bg-accent"
                        onClick={() => setNavMenuOpen(false)}
                      >
                        {sub.label}
                      </Link>
                    ))}
                    <div className="border-t border-border my-2" />
                  </>
                ) : item.href ? (
                  <Link
                    href={item.href}
                    className="block text-sm font-medium text-foreground hover:text-muted-foreground transition-colors py-2 px-2 rounded hover:bg-accent"
                    onClick={() => setNavMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ) : null}
              </div>
            ))}
          </nav>
        </div>
      )}

      {hasBreadcrumbs && <BreadcrumbBar breadcrumbs={breadcrumbs} accessory={breadcrumbAccessory} />}
    </header>
  )
}
